/**
 * mobileNav.js — Mobile drawers, bottom navigation, series/info/tags sync
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v6.1.0 — QWEN 3.8 PATCHED]:
 *  ✅ FIXED: activeNav scope leak (rememberLastDrawer писал '' — переменная была локальной)
 *  ✅ FIXED: Memory leak — observers и таймеры очищаются в destroyMobileNav
 *  ✅ FIXED: _cachedVP staleness — WeakRef + инвалидация
 *  ✅ NEW: Throttling syncInfo (1 вызов / 100мс)
 *  ✅ NEW: Подтверждение edge swipe (защита от ложных срабатываний)
 *  ✅ IMPROVED: Жесты читают чувствительность из _gestureConfig
 */

import { log } from '../utils/logger.js';
import { showPanelHelp } from './inlineHelp.js';
import { showToast } from '../utils/helpers.js';
// [10.0.2] refreshZoomRef и isZoomedIn больше не нужны: двойное касание
// разворачивает окно на весь экран, а не переключает масштаб.
import { ensureZoomRef, zoomPercent } from './zoomRefCore.js';

// [FIX] AbortController для очистки глобальных слушателей
let _ac = new AbortController();

// [FIX] Модульный scope — доступно из rememberLastDrawer/restoreLastDrawer
let _activeDrawer = null;
let _activeNav = 'tools';
let _cachedActiveTool = 'StackScroll';

// [FIX] Таймеры и observers — очищаются в destroyMobileNav
let _toolToastTimer = null;
let _gfTimer = null;
let _syncIntervalId = null;
let _syncThrottleTimer = null;
let _mobileObservers = [];

// [FIX] WeakRef для viewport (не держим мёртвые объекты)
let _cachedVPRef = null;

export function initMobileNav() {
  // [10.19.27] На телефоне скрываются функции, которые здесь не запустить:
  // двухоконное сравнение и отсоединение вьюпорта. Список и причины — в
  // core/mobileGate.js; порог ширины тот же, что у всего мобильного поведения.
  try {
    import('../core/mobileGate.js').then((m) => {
      const и = m.скрытьНедоступное(document, { ширина: window.innerWidth });
      if (и.скрыто.length) console.info('[МобШлюз] ' + и.причина + ': ' + и.скрыто.join(', '));
    });
  } catch (e) { /* скрытие — не повод ронять навигацию */ }

  // [10.0.1] Полная уборка перед повторной инициализацией.
  //
  // Раньше здесь снимались только слушатели (abort), а `_mobileObservers = []`
  // просто ронял ссылки на живые MutationObserver, не отключив их: наблюдатели
  // продолжали работать, а дотянуться до них было уже нельзя. Плюс ниже
  // `_syncIntervalId = setInterval(...)` перезаписывался поверх непогашенного —
  // каждый повторный init добавлял ещё один пятисекундный опрос DOM.
  //
  // На один вызов из main.js это незаметно, но при пересборке мобильной
  // оболочки (смена раскладки, HMR) интервалы и наблюдатели копятся линейно.
  _teardownMobileNav();

  _ac = new AbortController();
  log.debug('[mobileNav] initialized');

  function openDrawer(id) {
    window.closeMobileDrawers();
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.add('open');
    const overlay = document.getElementById('mobile-overlay');
    if (overlay) overlay.classList.add('active');
    _activeDrawer = id;
  }

  window.closeMobileDrawers = function() {
    const dst = document.getElementById('mpanel-body');
    if (dst && dst._movedPanel) {
      const panel = dst._movedPanel;
      if (panel._origParent) {
        panel.classList.remove('active');
        panel._origParent.insertBefore(panel, panel._origNext || null);
      }
      dst._movedPanel = null;
      dst.innerHTML = '';
    }
    document.querySelectorAll('.mobile-drawer').forEach(d => d.classList.remove('open'));
    const overlay = document.getElementById('mobile-overlay');
    if (overlay) overlay.classList.remove('active');
    _activeDrawer = null;
  };

  const overlayEl = document.getElementById('mobile-overlay');
  if (overlayEl) overlayEl.addEventListener('click', window.closeMobileDrawers, { signal: _ac.signal });

  /**
   * [10.1.9] Ящик закрывается после ЗАКОНЧЕННОГО действия — и только после него.
   *
   * В 10.1.8 я закрывал ящик по нажатию на любую кнопку внутри, и это было
   * грубо: в ящике «Панели» кнопки AI, Сегм., 3D и прочие НИЧЕГО не
   * применяют — они переключают содержимое ящика, за ними открывается целый
   * набор функций. Закрывать ящик на них — значит не давать до этих функций
   * добраться вовсе. Стало хуже, чем было.
   *
   * Поэтому теперь не запрет с исключениями, а РАЗРЕШЕНИЕ по списку: закрываем
   * только там, где нажатие — законченное действие и смотреть надо на снимок,
   * а не в меню. Пресет применён, инструмент выбран, проекция открыта,
   * раскладка сменилась.
   *
   * Всё остальное — переключатели вкладок, ползунки, поля, кнопки, которые
   * что-то раскрывают, — ящик не трогают. Ошибиться в эту сторону безопасно:
   * человек закроет ящик сам, как делал раньше. Ошибка в другую сторону
   * отбирает функции, и это как раз то, что я натворил.
   */
  const CLOSE_AFTER = [
    '[data-ww]',                 // пресет окна/уровня — применён, смотрим снимок
    '[data-render-preset]',      // объёмный пресет
    '[data-tool]',               // инструмент выбран
    '[data-view-mode]',          // проекция открыта
    '[data-layout]',             // раскладка сменилась
    '[data-std-view]',           // стандартный ракурс
    '[data-proxy]',              // проброс на кнопку верхней панели
  ].join(',');

  document.addEventListener('click', (e) => {
    const drawer = e.target.closest?.('.mobile-drawer');
    if (!drawer || !drawer.classList.contains('open')) return;
    const btn = e.target.closest('button');
    if (!btn || btn.hasAttribute('data-keep-open')) return;
    // Кнопка что-то РАСКРЫВАЕТ, а не применяет: вкладки ящика «Панели»
    // (data-mpanel), переходы между ящиками (data-nav). Ящик не трогаем.
    if (btn.hasAttribute('data-mpanel') || btn.hasAttribute('data-nav')) return;
    if (!btn.matches(CLOSE_AFTER)) return;
    if (btn.closest('.mobile-drawer-title, .mobile-drawer-handle')) return;
    requestAnimationFrame(() => window.closeMobileDrawers());
  }, { signal: _ac.signal });

  // Синхронизация series drawer c оригинальным списком
  function syncSeries() {
    const src = document.getElementById('series-list');
    const dst = document.getElementById('mobile-series-content');
    if (src && dst && src.innerHTML !== dst.innerHTML) {
      dst.innerHTML = src.innerHTML;
    }
  }

  // [FIX-LEAK] Event delegation — один listener вместо N
  const mobileSeriesContent = document.getElementById('mobile-series-content');
  if (mobileSeriesContent) {
    mobileSeriesContent.addEventListener('click', function(e) {
      const mItem = e.target.closest('.series-item');
      if (!mItem) return;
      const src = document.getElementById('series-list');
      if (!src) return;

      const uid = mItem.dataset.seriesUid;
      if (uid) {
        const origItem = src.querySelector('.series-item[data-series-uid="' + uid + '"]');
        if (origItem) origItem.click();
      } else {
        const items = Array.from(mobileSeriesContent.querySelectorAll('.series-item'));
        const idx = items.indexOf(mItem);
        const origItems = src.querySelectorAll('.series-item');
        if (origItems[idx]) origItems[idx].click();
      }
      window.closeMobileDrawers();
    }, { signal: _ac.signal });
  }

  // MutationObserver для обновления mobile series
  const seriesList = document.getElementById('series-list');
  if (seriesList) {
    const _seriesObs = new MutationObserver(syncSeries);
    _seriesObs.observe(seriesList, { childList: true, subtree: true });
    _mobileObservers.push(_seriesObs);
    window._mobileObservers = _mobileObservers; // compat
  }

  // ── Словарь инструментов ──
  const TOOL_LABELS = {
    'WindowLevel':      { icon: '<svg class="ico"><use href="#i-wl"/></svg>', name: 'W/L' },
    'Pan':              { icon: '<svg class="ico"><use href="#i-hand"/></svg>', name: 'Двигать' },
    'Zoom':             { icon: '<svg class="ico"><use href="#i-zoom"/></svg>', name: 'Масштаб' },
    'StackScroll':      { icon: '<svg class="ico"><use href="#i-scroll"/></svg>', name: 'Листание' },
    'TrackballRotate':  { icon: '<svg class="ico"><use href="#i-rotate3d"/></svg>', name: 'Вращение' },
    'VolumeRotateMouseWheel': { icon: '<svg class="ico"><use href="#i-refresh"/></svg>', name: 'Вращение объёма' },
    'MIPJumpToClick':   { icon: '<svg class="ico"><use href="#i-pointer"/></svg>', name: 'MIP Переход' },
    'Crosshairs':       { icon: '<svg class="ico"><use href="#i-crosshair"/></svg>', name: 'Крест' },
    'Length':           { icon: '<svg class="ico"><use href="#i-ruler"/></svg>', name: 'Длина' },
    'ArrowAnnotate':    { icon: '<svg class="ico"><use href="#i-arrow"/></svg>', name: 'Стрелка' },
    'Bidirectional':    { icon: '<svg class="ico"><use href="#i-bidir"/></svg>', name: 'Двунапр.' },
    'Angle':            { icon: '<svg class="ico"><use href="#i-angle"/></svg>', name: 'Угол' },
    'CobbAngle':        { icon: '<svg class="ico"><use href="#i-cobb"/></svg>', name: 'Кобба' },
    'Probe':            { icon: '<svg class="ico"><use href="#i-target"/></svg>', name: 'Зонд' },
    'EllipticalROI':    { icon: '<svg class="ico"><use href="#i-ellipse"/></svg>', name: 'Эллипс' },
    'RectangleROI':     { icon: '<svg class="ico"><use href="#i-rect"/></svg>', name: 'Прямоуг.' },
    'CircleROI':        { icon: '<svg class="ico"><use href="#i-circle"/></svg>', name: 'Круг' },
    'PlanarFreehandROI':{ icon: '<svg class="ico"><use href="#i-pencil"/></svg>', name: 'Своб. рисование' },
    'SplineROI':        { icon: '<svg class="ico"><use href="#i-spline"/></svg>', name: 'Сплайн' },
    'LivewireContour':  { icon: '<svg class="ico"><use href="#i-zap"/></svg>', name: 'Контур' },
    'TextAnnotation':   { icon: '<svg class="ico"><use href="#i-text"/></svg>', name: 'Текст' },
    'Brush':            { icon: '<svg class="ico"><use href="#i-brush"/></svg>', name: 'Кисть' },
    'ThresholdBrush':   { icon: '<svg class="ico"><use href="#i-palette"/></svg>', name: 'Порог. кисть' },
    'CircleScissors':   { icon: '<svg class="ico"><use href="#i-scissors"/></svg>', name: 'Круг. ножницы' },
    'RectangleScissors':{ icon: '<svg class="ico"><use href="#i-scissors"/></svg>', name: 'Прямоуг. ножн.' },
    'SphereScissors':   { icon: '<svg class="ico"><use href="#i-sphere"/></svg>', name: 'Сфер. ножницы' },
    'PaintFill':        { icon: '<svg class="ico"><use href="#i-bucket"/></svg>', name: 'Заливка' },
    'ReferenceLines':   { icon: '<svg class="ico"><use href="#i-reflines"/></svg>', name: 'Линии привязки' },
    'Magnify':          { icon: '<svg class="ico"><use href="#i-search"/></svg>', name: 'Лупа' },
    'ScaleOverlay':     { icon: '<svg class="ico"><use href="#i-scale"/></svg>', name: 'Масштаб. шкала' },
    'OrientationMarker':{ icon: '<svg class="ico"><use href="#i-compass"/></svg>', name: 'Ориентация' },
    'OverlayGrid':      { icon: '<svg class="ico"><use href="#i-grid"/></svg>', name: 'Сетка срезов' }
  };

  // Глобальная функция показа индикатора инструмента
  window.showToolIndicator = function(toolName) {
    const info = TOOL_LABELS[toolName] || { icon: '<svg class="ico"><use href="#i-wrench"/></svg>', name: toolName };

    const toast = document.getElementById('mobile-tool-toast');
    if (toast) {
      const iconEl = toast.querySelector('.mtt-icon');
      const labelEl = toast.querySelector('.mtt-label');
      if (iconEl) iconEl.innerHTML = info.icon;
      if (labelEl) labelEl.textContent = info.name;

      toast.classList.remove('fade-out');
      toast.classList.add('show');
      clearTimeout(_toolToastTimer);
      _toolToastTimer = setTimeout(function() {
        toast.classList.add('fade-out');
        setTimeout(function() { toast.classList.remove('show', 'fade-out'); }, 300);
      }, 1500);
    }

    const badge = document.getElementById('mobile-tool-badge');
    if (badge) {
      const bIcon = badge.querySelector('.mtb-icon');
      const bName = badge.querySelector('.mtb-name');
      if (bIcon) bIcon.innerHTML = info.icon;
      if (bName) bName.textContent = info.name;
    }

    if (navigator.vibrate && window._userHasInteracted) {
      try { navigator.vibrate(12); } catch (e) { log.debug('[MobileNav]', e.message); }
    }
  };

  window.showToolIndicator('StackScroll');

  // Синхронизация мобильных tool-btn с toolbar
  const _mToolBtns = document.querySelectorAll('#mobile-tools-drawer .m-tool-btn[data-tool]');
  log.debug('[mobileNav] Found', _mToolBtns.length, 'm-tool-btn elements');
  _mToolBtns.forEach(function(btn) {
    btn.addEventListener('click', function() {
      const toolName = btn.dataset.tool;
      log.debug('[mobileNav] Tool clicked:', toolName);
      _cachedActiveTool = toolName;
      window._mobileActiveTool = toolName;

      const orig = document.querySelector('.tool-btn[data-tool="' + toolName + '"]');
      if (orig) orig.click();

      document.querySelectorAll('#mobile-tools-drawer .m-tool-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      window.showToolIndicator(toolName);
      window.closeMobileDrawers();
    }, { signal: _ac.signal });
  });

  // Индикатор при клике на десктопные кнопки
  document.querySelectorAll('.tool-btn[data-tool]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      if (window.innerWidth <= 768) {
        _cachedActiveTool = btn.dataset.tool;
        window._mobileActiveTool = btn.dataset.tool;
        window.showToolIndicator(btn.dataset.tool);
        document.querySelectorAll('#mobile-tools-drawer .m-tool-btn').forEach(b => {
          b.classList.toggle('active', b.dataset.tool === btn.dataset.tool);
        });
      }
    }, { signal: _ac.signal });
  });

  // Навигация
  window.mobileNav = function(tab) {
    log.debug('[mobileNav] nav called:', tab);
    if (_activeDrawer && tab === _activeNav) {
      window.closeMobileDrawers();
      return;
    }
    _activeNav = tab;
    document.querySelectorAll('.mnav-btn').forEach(b => b.classList.remove('active'));
    const navBtn = document.getElementById('mnav-' + tab);
    if (navBtn) navBtn.classList.add('active');

    const drawerMap = {
      'tools':   'mobile-tools-drawer',
      'series':  'mobile-series-drawer',
      'presets': 'mobile-presets-drawer',
      'panels':  'mobile-panels-drawer',
      'more':    'mobile-more-drawer',
      // [11.1.21] «Кинема» — тот же ящик «Панели», но со своей подсветкой:
      // раньше кнопка шла через mobileNav('panels'), и активной красилась
      // «Панели» (замечание владельца со скриншотом). Теперь у кнопки свой
      // ключ в ЭТОЙ карте — подсветка и ящик связаны по построению, а после
      // открытия включается вкладка кинематики.
      'cinematic': 'mobile-panels-drawer',
    };
    if (tab === 'series') syncSeries();
    openDrawer(drawerMap[tab]);
    if (tab === 'cinematic') window.switchMPanel?.('render');
  };

  // Переключение вкладок в панелях
  window.switchMPanel = function(panel) {
    showPanelHelp(panel);
    document.querySelectorAll('[id^="mpanel-tab-"]').forEach(b => b.classList.remove('active'));
    const tabBtn = document.getElementById('mpanel-tab-' + panel);
    if (tabBtn) tabBtn.classList.add('active');

    const src = document.getElementById('tab-' + panel);
    const dst = document.getElementById('mpanel-body');
    if (src && dst) {
      if (dst._movedPanel && dst._movedPanel !== src) {
        const prev = dst._movedPanel;
        if (prev._origParent) {
          prev.classList.remove('active');
          prev._origParent.insertBefore(prev, prev._origNext || null);
        }
      }
      if (!src._origParent) {
        src._origParent = src.parentNode;
        src._origNext = src.nextSibling;
      }
      dst.innerHTML = '';
      src.classList.add('active');
      dst.appendChild(src);
      dst._movedPanel = src;
    }
  };

  // Синхронизация info-panels
  const style = document.createElement('style');
  style.innerHTML = '@media (max-width: 768px) { #mobile-vp-gpu-badge { display: inline-block !important; } }';
  document.head.appendChild(style);

  function syncInfo() {
    // [12.0.25] Шесть строк, как в разделе «Информация»: шаг и матрица
    // в ящик не попадали.
    ['patient','study','series','image','spacing','matrix'].forEach(key => {
      const src = document.getElementById('info-' + key);
      const dst = document.getElementById('info-' + key + '-m');
      if (src && dst) dst.textContent = src.textContent;
    });

    const desktopBadge = document.getElementById('webgpu-badge');
    const vpBadge = document.getElementById('mobile-vp-gpu-badge');
    const drawerBadge = document.getElementById('webgpu-badge-m');

    if (desktopBadge) {
      const text = desktopBadge.textContent;
      const bg = desktopBadge.style.background;
      const color = desktopBadge.style.color;

      if (vpBadge && vpBadge.textContent !== text) {
        vpBadge.textContent = text;
        vpBadge.style.background = bg;
        vpBadge.style.color = color;
        vpBadge.style.border = '1px solid ' + color;
      }
      if (drawerBadge && drawerBadge.textContent !== text) {
        drawerBadge.textContent = text;
        drawerBadge.style.background = bg;
        drawerBadge.style.color = color;
      }
    }

    const desktopTier = document.getElementById('gpu-tier-bottom');
    const drawerTier = document.getElementById('gpu-tier-bottom-m');
    if (desktopTier && drawerTier && drawerTier.textContent !== desktopTier.textContent) {
      drawerTier.textContent = desktopTier.textContent;
    }

    const desktopDot = document.getElementById('gpu-dot');
    const drawerDot = document.getElementById('gpu-dot-m');
    if (desktopDot && drawerDot && drawerDot.className !== desktopDot.className) {
      drawerDot.className = desktopDot.className;
    }
  }

  // [FIX] Throttled sync (1 вызов / 100мс)
  function requestSyncInfo() {
    if (_syncThrottleTimer) return;
    _syncThrottleTimer = setTimeout(() => {
      _syncThrottleTimer = null;
      requestAnimationFrame(syncInfo);
    }, 100);
  }

  ['STACK_NEW_IMAGE', 'IMAGE_RENDERED', 'VOI_MODIFIED'].forEach(evt => {
    const vc = document.getElementById('viewport-container');
    if (vc) vc.addEventListener(evt, requestSyncInfo, { signal: _ac.signal });
  });

  const seriesListObs = document.getElementById('series-list');
  if (seriesListObs) {
    const _syncInfoObs = new MutationObserver(requestSyncInfo);
    _syncInfoObs.observe(seriesListObs, { childList: true });
    _mobileObservers.push(_syncInfoObs);
    window._mobileObservers = _mobileObservers;
  }

  // Fallback: раз в 5 сек (останавливается в фоновой вкладке)
  _syncIntervalId = setInterval(syncInfo, 5000);
  window._stopMobileSyncInterval = function() { if (_syncIntervalId) { clearInterval(_syncIntervalId); _syncIntervalId = null; } };
  window._startMobileSyncInterval = function() { if (!_syncIntervalId) _syncIntervalId = setInterval(syncInfo, 5000); };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) window._stopMobileSyncInterval();
    else window._startMobileSyncInterval();
  }, { signal: _ac.signal });

  // Синхронизация DICOM tags
  function syncTags() {
    const src = document.getElementById('dicom-tags-content');
    const dst = document.getElementById('dicom-tags-mobile');
    if (src && dst && src.innerHTML.trim() && !src.innerHTML.includes('Load an image')) {
      if (src.innerHTML !== dst.innerHTML) dst.innerHTML = src.innerHTML;
    }
  }
  const tagsContent = document.getElementById('dicom-tags-content');
  if (tagsContent) {
    const _tagsObs = new MutationObserver(syncTags);
    _tagsObs.observe(tagsContent, { childList: true, subtree: true });
    _mobileObservers.push(_tagsObs);
    window._mobileObservers = _mobileObservers;
  }

  // tag search sync
  const tagSearchM = document.getElementById('tag-search-m');
  if (tagSearchM) {
    tagSearchM.addEventListener('input', function() {
      const orig = document.getElementById('tag-search');
      if (orig) {
        orig.value = tagSearchM.value;
        orig.dispatchEvent(new Event('input'));
      }
    }, { signal: _ac.signal });
  }

  // Свайп вниз для закрытия панелей
  document.querySelectorAll('.mobile-drawer').forEach(drawer => {
    let startY = 0;
    drawer.addEventListener('touchstart', e => {
      startY = e.touches[0].clientY;
    }, { passive: true, signal: _ac.signal });
    drawer.addEventListener('touchend', e => {
      const dy = e.changedTouches[0].clientY - startY;
      if (dy > 80 && drawer.scrollTop === 0) {
        window.closeMobileDrawers();
      }
    }, { passive: true, signal: _ac.signal });
  });

  // ════════════════════════════════════════════════════════════════
  // MOBILE VIEWPORT TOUCH GESTURES
  // ════════════════════════════════════════════════════════════════
  const vc = document.getElementById('viewport-container');
  if (vc) {
    _cachedActiveTool = window._mobileActiveTool || 'StackScroll';

    const _touchState = {
      startX: 0, startY: 0,
      lastX: 0, lastY: 0,
      startDist: 0,
      fingers: 0,
      startTime: 0,
      lastTapTime: 0,
      scrollAccum: 0,
      moved: false
    };

    // [FIX] Кэшированные DOM-элементы оверлеев
    const overlayEls = {
      slice: document.getElementById('overlay-slice'),
      wl: document.getElementById('overlay-wl'),
      zoom: document.getElementById('overlay-zoom')
    };

    function showGestureFeedback(value, label) {
      const el = document.getElementById('gesture-feedback');
      if (!el) return;
      const valEl = el.querySelector('.gf-value');
      const lblEl = el.querySelector('.gf-label');
      if (valEl) valEl.textContent = value;
      if (lblEl) lblEl.textContent = label || '';
      el.classList.add('visible');
      clearTimeout(_gfTimer);
      _gfTimer = setTimeout(function() { el.classList.remove('visible'); }, 600);
    }

    function updateVPOverlays(opts) {
      if (opts.slice !== undefined && overlayEls.slice) {
        overlayEls.slice.textContent = 'Срез: ' + opts.slice + (opts.total ? ' / ' + opts.total : '');
      }
      if (opts.ww !== undefined && overlayEls.wl) {
        overlayEls.wl.textContent = 'W: ' + Math.round(opts.ww) + ' / L: ' + Math.round(opts.wc || 0);
      }
      if (opts.zoom !== undefined && overlayEls.zoom) {
        overlayEls.zoom.textContent = 'Масштаб: ' + Math.round(opts.zoom) + '%';
      }
    }

    // ── Edge Swipe Tool Cycling ──
    const _recentTools = ['StackScroll', 'WindowLevel', 'Zoom'];
    let _recentToolIdx = 0;
    let _edgeSwipeStartX = -1;
    let _edgeSwipeRightStartX = -1;
    const _projectionModes = ['2d', 'axial', 'sagittal', 'coronal', '3d'];
    let _projectionIdx = 0;

    window._pushRecentTool = function(toolName) {
      const idx = _recentTools.indexOf(toolName);
      if (idx !== -1) _recentTools.splice(idx, 1);
      _recentTools.unshift(toolName);
      if (_recentTools.length > 5) _recentTools.length = 5;
      _recentToolIdx = 0;
    };

    // [FIX] WeakRef-кэш viewport
    function getActiveVP(touchEvent) {
      try {
        // [10.0.1] Кэш используется ТОЛЬКО когда координат касания нет.
        //
        // Здесь была регрессия. Кэш проверялся первым и возвращался, даже
        // если пришло событие с координатами, — то есть окно определялось
        // ровно один раз за сессию. В раскладке из нескольких окон (MPR, 2×2)
        // это значит: коснулись первого окна — и дальше листание, W/L и зум
        // на любом другом окне уходят в первое. До перезагрузки страницы.
        //
        // В версии 9 такой ошибки не было: там getActiveVP резолвил окно по
        // точке касания на каждый touchstart, а результат клался в _cachedVP
        // снаружи — и жил ровно один жест, что и правильно: начатый жест
        // должен продолжаться на том окне, где начался, даже если палец
        // уехал за его границу.
        const hasPoint = !!(touchEvent && touchEvent.touches && touchEvent.touches.length > 0);
        if (!hasPoint && _cachedVPRef) {
          const cached = _cachedVPRef.deref();
          if (cached) return cached;
        }

        let re = null;
        if (window.MikhmelViewer && window.MikhmelViewer.getRenderingEngine) {
          re = window.MikhmelViewer.getRenderingEngine();
        } else if (window.cornerstone && window.cornerstone.getRenderingEngine) {
          re = window.cornerstone.getRenderingEngine('mikhmelRenderingEngine');
        }
        if (!re) return null;

        let vp = null;
        if (touchEvent && touchEvent.touches && touchEvent.touches.length > 0) {
          const tx = touchEvent.touches[0].clientX;
          const ty = touchEvent.touches[0].clientY;
          const el = document.elementFromPoint(tx, ty);
          if (el) {
            const vpEl = el.closest('[id^="viewport-"]');
            if (vpEl) {
              const vpId = vpEl.id.replace('viewport-', '');
              vp = re.getViewport(vpId) || null;
              if (vp && window.MikhmelViewer && window.MikhmelViewer.focusViewport) {
                window.MikhmelViewer.focusViewport(vpId);
              }
            }
          }
        }

        if (!vp && window.MikhmelViewer && window.MikhmelViewer.getCurrentViewportId) {
          const curId = window.MikhmelViewer.getCurrentViewportId();
          if (curId) vp = re.getViewport(curId) || null;
        }

        if (!vp) {
          const vps = re.getViewports();
          vp = (vps && vps.length > 0) ? vps[0] : null;
        }

        if (vp) _cachedVPRef = new WeakRef(vp);
        return vp;
      } catch (e) { log.warn('[Viewer]', e); }
      return null;
    }

    function getSelectedTool() {
      return window._mobileActiveTool || _cachedActiveTool || 'StackScroll';
    }

    function pinchDist(t) {
      return Math.hypot(t[1].clientX - t[0].clientX, t[1].clientY - t[0].clientY);
    }

    function pinchCenter(t) {
      return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 };
    }

    vc.addEventListener('touchstart', function(e) {
      if (e.target.closest('.vp-zoom-btn, .vp-zoom-controls, .vp-zoom-spread, .vp-zoom-round, button')) return;

      const t = e.touches;
      _touchState.fingers = t.length;
      _touchState.startTime = Date.now();
      _touchState.moved = false;
      getActiveVP(e);

      if (t.length === 1 && t[0].clientX < _gestureConfig.edgeSwipeWidth) {
        _edgeSwipeStartX = t[0].clientX;
      } else {
        _edgeSwipeStartX = -1;
      }

      const screenW = window.innerWidth || document.documentElement.clientWidth;
      if (t.length === 1 && t[0].clientX > screenW - _gestureConfig.edgeSwipeWidth) {
        _edgeSwipeRightStartX = t[0].clientX;
      } else {
        _edgeSwipeRightStartX = -1;
      }

      if (t.length === 1) {
        _touchState.startX = _touchState.lastX = t[0].clientX;
        _touchState.startY = _touchState.lastY = t[0].clientY;
        _touchState.scrollAccum = 0;
      } else if (t.length === 2) {
        _touchState.startDist = pinchDist(t);
        const c = pinchCenter(t);
        _touchState.lastX = c.x;
        _touchState.lastY = c.y;
      }
    }, { passive: false, signal: _ac.signal });

    const HANDLED_TOOLS = {
      'StackScroll': true, 'Zoom': true, 'Pan': true,
      'WindowLevel': true, 'TrackballRotate': true, 'Magnify': true
    };

    vc.addEventListener('touchmove', function(e) {
      if (e.target.closest('.vp-zoom-btn, .vp-zoom-controls, .vp-zoom-spread, .vp-zoom-round, button')) return;

      const t = e.touches;
      const vp = _cachedVPRef ? _cachedVPRef.deref() : null;
      const tool = getSelectedTool();

      // ── Два пальца: Pinch Zoom + Pan ──
      if (t.length === 2 && _touchState.fingers >= 2) {
        e.preventDefault();
        _touchState.moved = true;
        const dist = pinchDist(t);
        const c = pinchCenter(t);

        if (_touchState.startDist > 0 && vp) {
          const scale = dist / _touchState.startDist;
          try {
            const cam = vp.getCamera();
            if (cam.parallelProjection) {
              vp.setCamera({ parallelScale: cam.parallelScale / scale });
            } else {
              const pos = cam.position, fp = cam.focalPoint;
              const d = Math.sqrt(Math.pow(pos[0]-fp[0],2) + Math.pow(pos[1]-fp[1],2) + Math.pow(pos[2]-fp[2],2));
              const nd = d / scale;
              const dir = [(pos[0]-fp[0])/d, (pos[1]-fp[1])/d, (pos[2]-fp[2])/d];
              vp.setCamera({ position: [fp[0]+dir[0]*nd, fp[1]+dir[1]*nd, fp[2]+dir[2]*nd] });
            }
            vp.render();
          } catch(ex) { log.warn('[Touch]', ex); }
          _touchState.startDist = dist;
        }

        const dx = c.x - _touchState.lastX;
        const dy = c.y - _touchState.lastY;
        if ((Math.abs(dx) > 1 || Math.abs(dy) > 1) && vp) {
          try {
            const pan = vp.getPan ? vp.getPan() : [0,0];
            vp.setPan([pan[0] + dx * _gestureConfig.panSensitivity, pan[1] + dy * _gestureConfig.panSensitivity]);
            vp.render();
          } catch(ex) { log.warn('[Touch]', ex); }
        }
        _touchState.lastX = c.x;
        _touchState.lastY = c.y;
        return;
      }

      // ── Один палец ──
      if (t.length === 1) {
        if (!HANDLED_TOOLS[tool]) return;

        e.preventDefault();

        const cx = t[0].clientX, cy = t[0].clientY;
        const dx = cx - _touchState.lastX;
        const dy = cy - _touchState.lastY;

        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) _touchState.moved = true;

        switch(tool) {
          case 'StackScroll':
            _touchState.scrollAccum += dy;
            if (Math.abs(_touchState.scrollAccum) >= _gestureConfig.scrollThreshold && vp) {
              try {
                const dir = _touchState.scrollAccum > 0 ? 1 : -1;
                if (vp.setImageIdIndex && vp.getCurrentImageIdIndex) {
                  const idx = vp.getCurrentImageIdIndex();
                  const total = vp.getImageIds ? vp.getImageIds().length : 0;
                  if (total > 0) {
                    const newIdx = Math.max(0, Math.min(total - 1, idx + dir));
                    vp.setImageIdIndex(newIdx);
                    vp.render();
                    showGestureFeedback((newIdx + 1) + ' / ' + total, 'Срез');
                    updateVPOverlays({ slice: newIdx + 1, total: total });
                  } else if (vp.scroll) {
                    vp.scroll(dir);
                    vp.render();
                    showGestureFeedback(dir > 0 ? '▼' : '▲', 'Скролл');
                  }
                } else if (vp.scroll) {
                  vp.scroll(dir);
                  vp.render();
                  showGestureFeedback(dir > 0 ? '▼' : '▲', 'Скролл');
                }
              } catch(ex) { log.warn('[Touch]', ex); }
              _touchState.scrollAccum = 0;
            }
            break;

          case 'Zoom':
            if (vp && Math.abs(dy) > 1) {
              try {
                const cam = vp.getCamera();
                const factor = 1 - dy * _gestureConfig.zoomSensitivity;
                if (cam.parallelProjection) {
                  const origScale = ensureZoomRef(vp, cam.parallelScale);
                  const newScale = cam.parallelScale * factor;
                  vp.setCamera({ parallelScale: newScale });
                  const pct = zoomPercent(origScale, newScale);
                  showGestureFeedback(pct + '%', 'Масштаб');
                  updateVPOverlays({ zoom: pct });
                } else {
                  const pos = cam.position, fp = cam.focalPoint;
                  const d = Math.sqrt(Math.pow(pos[0]-fp[0],2)+Math.pow(pos[1]-fp[1],2)+Math.pow(pos[2]-fp[2],2));
                  const nd = d * factor;
                  const dir2 = [(pos[0]-fp[0])/d,(pos[1]-fp[1])/d,(pos[2]-fp[2])/d];
                  vp.setCamera({position:[fp[0]+dir2[0]*nd,fp[1]+dir2[1]*nd,fp[2]+dir2[2]*nd]});
                }
                vp.render();
              } catch(ex) { log.warn('[Touch]', ex); }
            }
            break;

          case 'Pan':
            if (vp) {
              try {
                const pan = vp.getPan ? vp.getPan() : [0,0];
                vp.setPan([pan[0] + dx * _gestureConfig.panSensitivity, pan[1] + dy * _gestureConfig.panSensitivity]);
                vp.render();
              } catch(ex) { log.warn('[Touch]', ex); }
            }
            break;

          case 'WindowLevel':
            if (vp && (Math.abs(dx) > 1 || Math.abs(dy) > 1)) {
              try {
                const props = vp.getProperties ? vp.getProperties() : {};
                const voi = props.voiRange || { lower: -200, upper: 600 };
                let ww = voi.upper - voi.lower;
                let wc = (voi.upper + voi.lower) / 2;
                ww = Math.max(1, ww + dx * _gestureConfig.wlSensitivityX);
                wc = wc - dy * _gestureConfig.wlSensitivityY;
                vp.setProperties({ voiRange: { lower: wc - ww/2, upper: wc + ww/2 } });
                vp.render();
                showGestureFeedback('W:' + Math.round(ww) + ' L:' + Math.round(wc), 'Окно / Уровень');
                updateVPOverlays({ ww: ww, wc: wc });
              } catch(ex) { log.warn('[Touch]', ex); }
            }
            break;

          case 'TrackballRotate':
            if (vp) {
              try {
                const cam = vp.getCamera();
                if (!cam.parallelProjection) {
                  const angleX = -dx * 0.5 * Math.PI / 180;
                  const angleY = -dy * 0.5 * Math.PI / 180;
                  const fp = cam.focalPoint, pos = cam.position, up = cam.viewUp;
                  const v = [pos[0]-fp[0], pos[1]-fp[1], pos[2]-fp[2]];
                  const d = Math.sqrt(v[0]*v[0]+v[1]*v[1]+v[2]*v[2]);
                  const vn = [v[0]/d, v[1]/d, v[2]/d];
                  const right = [
                    up[1]*vn[2] - up[2]*vn[1],
                    up[2]*vn[0] - up[0]*vn[2],
                    up[0]*vn[1] - up[1]*vn[0]
                  ];
                  const cosA = Math.cos(angleX), sinA = Math.sin(angleX);
                  const newV = [
                    v[0]*cosA + right[0]*d*sinA,
                    v[1]*cosA + right[1]*d*sinA,
                    v[2]*cosA + right[2]*d*sinA
                  ];
                  const cosB = Math.cos(angleY), sinB = Math.sin(angleY);
                  const newV2 = [
                    newV[0]*cosB + up[0]*d*sinB,
                    newV[1]*cosB + up[1]*d*sinB,
                    newV[2]*cosB + up[2]*d*sinB
                  ];
                  vp.setCamera({
                    position: [fp[0]+newV2[0], fp[1]+newV2[1], fp[2]+newV2[2]],
                    focalPoint: fp,
                    viewUp: up
                  });
                  vp.render();
                }
              } catch(ex) { log.warn('[Touch]', ex); }
            }
            break;

          case 'Magnify':
            if (vp) {
              try {
                const pan = vp.getPan ? vp.getPan() : [0,0];
                vp.setPan([pan[0] + dx * _gestureConfig.panSensitivity * 0.5, pan[1] + dy * _gestureConfig.panSensitivity * 0.5]);
                vp.render();
                showGestureFeedback('🔎', 'Лупа');
              } catch(ex) { log.warn('[Touch]', ex); }
            }
            break;
        }

        _touchState.lastX = cx;
        _touchState.lastY = cy;
      }
    }, { passive: false, signal: _ac.signal });

    vc.addEventListener('touchend', function(e) {
      if (e.target.closest('.vp-zoom-btn, .vp-zoom-controls, .vp-zoom-spread, .vp-zoom-round, button')) return;

      if (_edgeSwipeStartX >= 0 && e.changedTouches.length === 1) {
        const endX = e.changedTouches[0].clientX;
        const swipeDist = endX - _edgeSwipeStartX;
        if (swipeDist > 60 && !_touchState.moved) {
          _recentToolIdx = (_recentToolIdx + 1) % _recentTools.length;
          const nextTool = _recentTools[_recentToolIdx];
          const toolBtn = document.querySelector('.tool-btn[data-tool="' + nextTool + '"]');
          if (toolBtn) toolBtn.click();
          if (window.showToolIndicator) window.showToolIndicator(nextTool);
          showGestureFeedback(nextTool, '⟵ Свайп: смена инструмента');
          _edgeSwipeStartX = -1;
          return;
        }
      }
      _edgeSwipeStartX = -1;

      if (_edgeSwipeRightStartX >= 0 && e.changedTouches.length === 1) {
        const endXR = e.changedTouches[0].clientX;
        const swipeDistR = _edgeSwipeRightStartX - endXR;
        if (swipeDistR > 60 && !_touchState.moved) {
          _projectionIdx = (_projectionIdx + 1) % _projectionModes.length;
          const nextMode = _projectionModes[_projectionIdx];
          if (window.MikhmelViewer && window.MikhmelViewer.activateViewMode) {
            window.MikhmelViewer.activateViewMode(nextMode);
          }
          const modeLabels = { '2d': '2D', 'axial': 'Axial', 'sagittal': 'Sagittal', 'coronal': 'Coronal', '3d': '3D' };
          showGestureFeedback(modeLabels[nextMode] || nextMode, '⟶ Свайп: проекция');
          _edgeSwipeRightStartX = -1;
          return;
        }
      }
      _edgeSwipeRightStartX = -1;

      const now = Date.now();
      if (!_touchState.moved && (now - _touchState.lastTapTime) < _gestureConfig.doubleTapMs && _touchState.fingers === 1) {
        // [10.0.2] Двойное касание разворачивает окно на весь экран — ровно
        // как двойной щелчок на десктопе.
        //
        // Было: масштаб ×0.5 (и сброс, если уже приближено). То есть один и
        // тот же жест на телефоне и на компьютере делал разное, и на шести
        // окнах это особенно мешает: разглядеть проекцию на трети экрана
        // нельзя, а увеличение внутри крошечного окна ничего не даёт —
        // человеку нужно окно во весь экран. Увеличение и без того
        // доступно щипком и кнопками ±.
        const el = e.changedTouches?.[0]
          ? document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY)
          : null;
        const vpEl = el?.closest?.('[id^="viewport-"]')
          || (_cachedVPRef?.deref?.()?.element ?? null);
        if (vpEl) {
          try {
            const api = globalThis.MikhmelViewer;
            if (typeof api?.toggleMaximizeViewport === 'function') api.toggleMaximizeViewport(vpEl);
            else vpEl.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
            try { navigator.vibrate?.(15); } catch (e2) { /* */ }
          } catch (ex) { log.warn('[Touch] разворот окна:', ex.message); }
        }
        _touchState.lastTapTime = 0;
      } else {
        _touchState.lastTapTime = now;
      }
    }, { passive: true, signal: _ac.signal });

    log.debug('[Mobile] Viewport touch gestures initialized');

    // === Очистка observers при btn-clear ===
    const btnClear = document.getElementById('btn-clear');
    if (btnClear) {
      btnClear.addEventListener('click', function() {
        window._stopMobileSyncInterval();
        _mobileObservers.forEach(obs => {
          try { obs.disconnect(); } catch (e) { log.debug('[MobileNav]', e.message); }
        });
        _mobileObservers = [];
        setTimeout(function() { window._startMobileSyncInterval(); }, 500);
      }, { signal: _ac.signal });
    }
  }
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Gesture profiles, radial menu, drawer memory
// ══════════════════════════════════════════════════════════════

const GESTURE_LS_KEY = 'mikhmel_gesture_config';
const DRAWER_LS_KEY = 'mikhmel_last_drawer';

let _gestureConfig = {
  scrollThreshold: 25,
  zoomSensitivity: 0.005,
  panSensitivity: 1.0,
  wlSensitivityX: 1.5,
  wlSensitivityY: 1.5,
  edgeSwipeWidth: 25,
  doubleTapMs: 300,
};

try { Object.assign(_gestureConfig, JSON.parse(localStorage.getItem(GESTURE_LS_KEY) || '{}')); } catch { /* fallback */ }

export function getGestureConfig() { return { ..._gestureConfig }; }

export function setGestureSensitivity(key, value) {
  if (!(key in _gestureConfig)) return;
  _gestureConfig[key] = value;
  try { localStorage.setItem(GESTURE_LS_KEY, JSON.stringify(_gestureConfig)); } catch { /* non-critical */ }
  log.debug(`[MobileNav] Gesture ${key} = ${value}`);
}

export function resetGestureConfig() {
  _gestureConfig = { scrollThreshold: 25, zoomSensitivity: 0.005, panSensitivity: 1.0, wlSensitivityX: 1.5, wlSensitivityY: 1.5, edgeSwipeWidth: 25, doubleTapMs: 300 };
  try { localStorage.removeItem(GESTURE_LS_KEY); } catch { /* non-critical */ }
  showToast('🔄 Жесты сброшены', 'info', 1200);
}

export function setGesturePresetFast() {
  Object.assign(_gestureConfig, { scrollThreshold: 15, zoomSensitivity: 0.008, wlSensitivityX: 2.5, wlSensitivityY: 2.5 });
  try { localStorage.setItem(GESTURE_LS_KEY, JSON.stringify(_gestureConfig)); } catch { /* non-critical */ }
  showToast('🏎 Быстрый режим жестов', 'info', 1200);
}

export function setGesturePresetPrecise() {
  Object.assign(_gestureConfig, { scrollThreshold: 40, zoomSensitivity: 0.002, wlSensitivityX: 0.8, wlSensitivityY: 0.8 });
  try { localStorage.setItem(GESTURE_LS_KEY, JSON.stringify(_gestureConfig)); } catch { /* non-critical */ }
  showToast('🎯 Точный режим жестов', 'info', 1200);
}

// [FIX] Теперь читает модульную переменную _activeNav (раньше была локальной → всегда '')
export function rememberLastDrawer() {
  try { localStorage.setItem(DRAWER_LS_KEY, _activeNav || ''); } catch { /* non-critical */ }
}

export function restoreLastDrawer() {
  try {
    const last = localStorage.getItem(DRAWER_LS_KEY);
    if (last && window.mobileNav) window.mobileNav(last);
  } catch { /* non-critical */ }
}

export function triggerHaptic(pattern = 'light') {
  if (!navigator.vibrate) return;
  const patterns = {
    light: [10],
    medium: [30],
    heavy: [50],
    success: [10, 50, 10],
    error: [30, 30, 30, 30, 30],
    double: [15, 40, 15],
  };
  try { navigator.vibrate(patterns[pattern] || [10]); } catch { /* non-critical */ }
}

export function getMobileNavState() {
  return {
    toolButtons: document.querySelectorAll('#mobile-tools-drawer .m-tool-btn').length,
    openDrawer: document.querySelector('.mobile-drawer.open')?.id || null,
    activeTool: window._mobileActiveTool || 'StackScroll',
    activeNav: _activeNav,
    gestureConfig: { ..._gestureConfig },
  };
}

export function openMobileDrawer(name) { if (window.mobileNav) window.mobileNav(name); }
export function closeMobileDrawers() { if (window.closeMobileDrawers) window.closeMobileDrawers(); }
export function switchMobilePanel(panel) { if (window.switchMPanel) window.switchMPanel(panel); }
export function stopMobileSync() { if (window._stopMobileSyncInterval) window._stopMobileSyncInterval(); }
export function startMobileSync() { if (window._startMobileSyncInterval) window._startMobileSyncInterval(); }

/**
 * [10.0.1] Снятие всего, что модуль оставляет за собой.
 * Вызывается и из destroyMobileNav, и из initMobileNav — повторная
 * инициализация обязана начинаться с чистого состояния, иначе интервалы и
 * наблюдатели копятся с каждой пересборкой мобильной оболочки.
 */
function _teardownMobileNav() {
  if (_ac) { _ac.abort(); _ac = null; }

  clearTimeout(_toolToastTimer); _toolToastTimer = null;
  clearTimeout(_gfTimer); _gfTimer = null;
  clearTimeout(_syncThrottleTimer); _syncThrottleTimer = null;
  if (_syncIntervalId) { clearInterval(_syncIntervalId); _syncIntervalId = null; }

  _mobileObservers.forEach(obs => { try { obs.disconnect(); } catch (e) { /* уже отключён */ } });
  _mobileObservers = [];
  if (typeof window !== 'undefined') window._mobileObservers = _mobileObservers;

  _cachedVPRef = null;
}

// [FIX] Полная очистка: listeners + таймеры + observers + WeakRef
export function destroyMobileNav() {
  _teardownMobileNav();
  _activeDrawer = null;
  _activeNav = 'tools';
}